import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  MenuList,
  OutlinedInput,
  Popover,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { alpha } from "@mui/material/styles";
import * as React from "react";
import { COLORS } from "../../theme/theme-options";

export default function AppHeader() {
  const [userPopoverAnchor, setUserPopoverAnchor] =
    React.useState<HTMLElement | null>(null);

  const handleUserClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setUserPopoverAnchor(event.currentTarget);
  };

  const handleUserPopoverClose = () => {
    setUserPopoverAnchor(null);
  };

  const handleLogout = () => {
    handleUserPopoverClose();
    window.localStorage.clear();
    window.location.reload();
  };

  return (
    <Box
      component="header"
      sx={(theme) => ({
        position: "sticky",
        top: 0,
        zIndex: theme.zIndex.appBar,
        height: 64,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: { xs: 1.5, sm: 2.5, md: 4 },
        width: "100%",
        backgroundColor: alpha("#060d1a", 0.8),
        backdropFilter: "blur(16px)",
        borderBottom: `1px solid ${alpha(COLORS.grey[700], 0.4)}`,
      })}
    >
      <Box
        display="flex"
        alignItems="center"
        width="100%"
        gap={1.5}
        justifyContent="space-between"
      >
        <OutlinedInput
          size="small"
          placeholder="Search..."
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          }
          sx={{
            width: { xs: 120, sm: 180, md: 260 },
            height: 36,
            fontSize: 13,
            color: COLORS.grey[200],
            backgroundColor: alpha(COLORS.grey[800], 0.35),
            borderRadius: 2,
            "& fieldset": {
              borderColor: alpha(COLORS.grey[700], 0.5),
            },
            "&:hover fieldset": {
              borderColor: alpha(COLORS.grey[600], 0.7),
            },
            "&.Mui-focused fieldset": {
              borderColor: alpha(COLORS.primary[400], 0.5),
            },
            "& .MuiInputAdornment-root": {
              color: alpha(COLORS.grey[300], 0.8),
            },
          }}
        />
        <Box display="flex" gap={1}>
          <IconButton
            size="small"
            onClick={handleUserClick}
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              border: `1px solid ${alpha(COLORS.primary[400], 0.25)}`,
              background: `linear-gradient(135deg, ${alpha(COLORS.primary[500], 0.18)}, ${alpha(
                COLORS.primary[600],
                0.12,
              )})`,
              color: COLORS.primary[300],
              "&:hover": {
                borderColor: alpha(COLORS.primary[400], 0.35),
                boxShadow: `0 0 18px ${alpha(COLORS.primary[500], 0.12)}`,
              },
            }}
          >
            <PersonOutlineOutlinedIcon fontSize="small" />
          </IconButton>
          <Popover
            open={Boolean(userPopoverAnchor)}
            anchorEl={userPopoverAnchor}
            onClose={handleUserPopoverClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  minWidth: 170,
                  border: `1px solid ${alpha(COLORS.grey[700], 0.7)}`,
                  backgroundColor: alpha("#060d1a", 0.96),
                  backdropFilter: "blur(14px)",
                },
              },
            }}
          >
            <MenuList dense sx={{ p: 0.5 }}>
              <MenuItem
                onClick={() => {
                  handleLogout();
                }}
                sx={{
                  borderRadius: 1,
                  color: COLORS.grey[100],
                  gap: 1,
                  "&:hover": {
                    backgroundColor: alpha(COLORS.grey[700], 0.45),
                  },
                }}
              >
                <LogoutRoundedIcon fontSize="small" />
                Logout
              </MenuItem>
            </MenuList>
          </Popover>
        </Box>
      </Box>
    </Box>
  );
}
